import { useEffect, useMemo } from 'react';
import { Select } from './Select';
import { useYardBlocksQuery } from '../../hooks';
import { getYardBlocksFromBlocksScene } from '../yard-place-scene/validate-container-place';
import { LoadingIcon } from '../input';
import { INPUT_ERRORS, optionalTiers, tiers } from '../../const';

let hasPlace = false;
export const getHasYardPlace = () => hasPlace;

interface YardPlaceSelectsProps {
  yardId?: string;
  clientYardId?: string;
  containerId?: string;
  inventoryId?: string;
  blocksScene?: string;
  containerLength?: number;
  block: string;
  row?: string;
  slot?: string;
  tier?: string;
  setBlock: (block: any) => void;
  setRow?: (row: any) => void;
  setSlot?: (slot: any) => void;
  setTier?: (tier: any) => void;
  isSubmitClicked?: boolean;
  isOptional?: boolean;
  isOptionalTier?: boolean;
  withDefaultValues?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  disabledTier?: boolean;
  disabledRequest?: boolean;
  hideHelperText?: boolean;
}

export const YardPlaceSelects: React.FC<YardPlaceSelectsProps> = ({
  yardId,
  clientYardId,
  containerId,
  inventoryId,
  blocksScene,
  containerLength,
  block,
  row,
  slot,
  tier,
  setBlock,
  setRow,
  setSlot,
  setTier,
  isSubmitClicked,
  isOptional = false,
  isOptionalTier = false,
  withDefaultValues,
  isLoading,
  disabled,
  disabledTier,
  disabledRequest,
  hideHelperText,
}: YardPlaceSelectsProps) => {
  const isReadOnlyDisplay = !!disabledRequest && !blocksScene;

  const sceneData = useMemo(
    () =>
      blocksScene
        ? getYardBlocksFromBlocksScene(blocksScene, {
            containerLength: Number(containerLength) || 20,
            excludeContainerId: containerId ?? '',
            selectedBlock: block,
            selectedRow: row,
          })
        : [],
    [blocksScene, containerLength, containerId, block, row],
  );

  const { data: queryData = [], isFetching: isQueryFetching } = useYardBlocksQuery(
    ['yard-short', yardId || clientYardId || containerId, inventoryId],
    {
      yardId,
      containerId,
      clientYardId,
      inventoryId,
    },
    { enabled: !blocksScene && !disabledRequest },
  );

  const data = blocksScene ? sceneData : queryData;
  const isFetching = blocksScene ? false : isQueryFetching;

  const dataRows = useMemo(() => {
    const rows = data.find((item) => item.block === block)?.rows;
    return rows?.map(String);
  }, [data, block]);
  const dataSlots = useMemo(() => {
    const slots = data.find((item) => item.block === block)?.slots;
    return slots?.map(String);
  }, [data, block]);

  const blockOptions = useMemo(
    () => (isFetching ? [] : data.map((item) => item.block)),
    [data, isFetching],
  );
  const blockValue = block != null && block !== '' ? String(block) : '';
  const rowValue = row != null && row !== '' ? String(row) : '';
  const slotValue = slot != null && slot !== '' ? String(slot) : '';
  const tierValue = tier != null && tier !== '' ? String(tier) : '';
  const safeBlock = blockValue && blockOptions.includes(blockValue) ? blockValue : '';
  const safeRow = rowValue && dataRows?.includes(rowValue) ? rowValue : '';
  const safeSlot = slotValue && dataSlots?.includes(slotValue) ? slotValue : '';
  const displayBlock = isReadOnlyDisplay ? blockValue : safeBlock;
  const displayRow = isReadOnlyDisplay ? rowValue : safeRow;
  const displaySlot = isReadOnlyDisplay ? slotValue : safeSlot;
  const displayTier = isReadOnlyDisplay ? tierValue : tier ?? '';

  const blockSelectOptions = useMemo(() => {
    if (isReadOnlyDisplay && blockValue) {
      return [{ label: blockValue, value: blockValue }];
    }

    return isFetching ? [] : data.map((item) => ({ label: item.block, value: item.block }));
  }, [blockValue, data, isReadOnlyDisplay, isFetching]);

  const rowSelectOptions = useMemo(() => {
    if (isReadOnlyDisplay && rowValue) {
      return [{ label: rowValue, value: rowValue }];
    }

    return isFetching ? [] : dataRows?.map((item) => ({ label: item, value: item })) ?? [];
  }, [dataRows, isReadOnlyDisplay, isFetching, rowValue]);

  const slotSelectOptions = useMemo(() => {
    if (isReadOnlyDisplay && slotValue) {
      return [{ label: slotValue, value: slotValue }];
    }

    return isFetching ? [] : dataSlots?.map((item) => ({ label: item, value: item })) ?? [];
  }, [dataSlots, isReadOnlyDisplay, isFetching, slotValue]);

  const tierSelectOptions = useMemo(() => {
    if (isReadOnlyDisplay && tierValue) {
      return [{ label: tierValue, value: tierValue }];
    }

    return isFetching ? [] : isOptional || isOptionalTier ? optionalTiers : tiers;
  }, [disabledRequest, isFetching, isOptional, isOptionalTier, isReadOnlyDisplay, tierValue]);

  useEffect(() => {
    if (isReadOnlyDisplay) return;

    if (!blockValue || !blockOptions.length) return;

    if (!blockOptions.includes(blockValue)) {
      setBlock('');
      if (setRow) setRow('');
      if (setSlot) setSlot('');
    }
  }, [blockValue, blockOptions, isReadOnlyDisplay, setBlock, setRow, setSlot]);

  useEffect(() => {
    if (isReadOnlyDisplay) return;

    if (!rowValue || !setRow || !dataRows?.length) return;

    if (!dataRows.includes(rowValue)) {
      setRow('');
      if (setSlot) setSlot('');
    }
  }, [dataRows, isReadOnlyDisplay, rowValue, setRow, setSlot]);

  useEffect(() => {
    if (isReadOnlyDisplay) return;

    if (!rowValue || !slotValue || !setSlot) return;

    if (!dataSlots?.length) return;

    if (!dataSlots.includes(slotValue)) {
      setSlot('');
    }
  }, [dataSlots, isReadOnlyDisplay, rowValue, slotValue, setSlot]);

  useEffect(() => {
    if (setRow && row != null && row !== '' && String(row) !== row) {
      setRow(String(row));
    }
  }, [row, setRow]);

  useEffect(() => {
    if (setSlot && slot != null && slot !== '' && String(slot) !== slot) {
      setSlot(String(slot));
    }
  }, [slot, setSlot]);

  useEffect(() => {
    if (block != null && block !== '' && String(block) !== block) {
      setBlock(String(block));
    }
  }, [block, setBlock]);

  useEffect(() => {
    if (withDefaultValues) return;

    if (setRow) setRow('');
    if (setSlot) setSlot('');
    setBlock('');
  }, [yardId, clientYardId, containerId, blocksScene]);

  useEffect(() => {
    if (isFetching) {
      hasPlace = true;
      return;
    }

    hasPlace = !!data.length;
  }, [data.length, isFetching]);

  if (!isFetching && !data.length && !disabledRequest) return <></>;
  return (
    <>
      <Select
        label={'Block' + (isOptional || disabled ? '' : '*')}
        isLoading={isLoading}
        options={blockSelectOptions}
        value={isFetching && !isReadOnlyDisplay ? '' : displayBlock}
        onChange={(e) => {
          if (setRow) setRow('');
          if (setSlot) setSlot('');

          setBlock(e.target.value);
        }}
        IconComponent={isFetching || isLoading ? LoadingIcon : undefined}
        error={isSubmitClicked && !block}
        helperText={
          hideHelperText ? undefined : isSubmitClicked && !block && INPUT_ERRORS.REQUIRED.BLOCK
        }
        disabled={disabled}
      />
      {setRow && (
        <Select
          label={'Row' + (isOptional || disabled ? '' : '*')}
          isLoading={isLoading}
          options={rowSelectOptions}
          value={isFetching && !isReadOnlyDisplay ? '' : displayRow}
          onChange={(e) => {
            setRow(e.target.value);
            if (setSlot) setSlot('');
          }}
          disabled={disabled || !block}
          error={isSubmitClicked && !row}
          helperText={
            hideHelperText ? undefined : isSubmitClicked && !row && INPUT_ERRORS.REQUIRED.ROW
          }
        />
      )}
      {setSlot && (isReadOnlyDisplay || dataSlots) && (
        <Select
          label={'Slot' + (isOptional || disabled ? '' : '*')}
          isLoading={isLoading}
          options={slotSelectOptions}
          value={isFetching && !isReadOnlyDisplay ? '' : displaySlot}
          onChange={(e) => setSlot(e.target.value)}
          disabled={disabled || !row}
          error={isSubmitClicked && !slot}
          helperText={
            hideHelperText ? undefined : isSubmitClicked && !slot && INPUT_ERRORS.REQUIRED.SLOT
          }
        />
      )}
      {setTier && (isReadOnlyDisplay || dataSlots) && (
        <Select
          label={'Tier' + (isOptional || isOptionalTier || disabled ? '' : '*')}
          placeholder={isOptional || isOptionalTier ? 'On Top' : ''}
          isLoading={isLoading}
          options={tierSelectOptions}
          value={isFetching && !isReadOnlyDisplay ? '' : displayTier}
          onChange={(e) => setTier(e.target.value)}
          error={isSubmitClicked && !isOptionalTier && !tier && !disabledTier}
          helperText={
            hideHelperText
              ? undefined
              : isSubmitClicked && !isOptionalTier && !tier && INPUT_ERRORS.REQUIRED.TIER
          }
          disabled={disabled || disabledTier}
        />
      )}
    </>
  );
};
